import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChefHat } from "lucide-react";

// Shared frame for every /resources/* page. Owns the top brand strip,
// the "Kitchen Operations & Formulas" eyebrow, and a prev/next hub nav
// footer. Content pages just pass their body as children.

const HUB = [
  { slug: "food-cost", label: "Food Cost", href: "/resources/food-cost" },
  { slug: "labour-cost", label: "Labour Cost", href: "/resources/labour-cost" },
  { slug: "recipe-formulas", label: "Recipe Formulas", href: "/resources/recipe-formulas" },
  { slug: "bulk-conversion", label: "Bulk Conversion", href: "/resources/bulk-conversion" },
];

export default function ResourceLayout({ activeSlug, title, lede, children }) {
  const idx = HUB.findIndex((h) => h.slug === activeSlug);
  const prev = idx > 0 ? HUB[idx - 1] : null;
  const next = idx >= 0 && idx < HUB.length - 1 ? HUB[idx + 1] : null;

  return (
    <div className="min-h-screen">
      <header className="border-b border-border/60">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="resource-brand">
            <img
              src="/brand/mark-256.png"
              alt="Culinaire Specialist"
              className="w-9 h-9 rounded-full object-contain"
              style={{ background: "#0a0806", boxShadow: "0 0 0 1px rgba(232,207,154,0.28)" }}
            />
            <span className="font-display text-lg">Culinaire Specialist</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <Link to="/blog" className="px-3 py-2 rounded-full hover:bg-white/5 text-white/70">Journal</Link>
            <Link to="/resources" className="px-3 py-2 rounded-full hover:bg-white/5 text-white/70">Resources</Link>
            <Link to="/pricing" className="px-3 py-2 rounded-full hover:bg-white/5 text-white/70">Pricing</Link>
            <Link to="/register" className="ml-2 gold-cta px-4 h-9 rounded-full inline-flex items-center gap-1.5 font-medium text-sm">
              Start free
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="label-eyebrow flex items-center gap-2 mb-4">
          <ChefHat className="w-3.5 h-3.5" />
          Kitchen operations and formulas
        </div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight">{title}</h1>
        {lede ? <p className="mt-4 text-lg text-white/75 max-w-3xl">{lede}</p> : null}

        <div className="mt-10 space-y-14">{children}</div>

        <nav className="mt-16 pt-8 border-t border-border/60 flex flex-wrap items-center justify-between gap-4 text-sm">
          {prev ? (
            <Link to={prev.href} className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors" data-testid="resource-prev">
              <ArrowLeft className="w-4 h-4" /> {prev.label}
            </Link>
          ) : <span />}
          <Link to="/resources" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors">
            All resources
          </Link>
          {next ? (
            <Link to={next.href} className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors" data-testid="resource-next">
              {next.label} <ArrowRight className="w-4 h-4" />
            </Link>
          ) : <span />}
        </nav>
      </main>

      <footer className="border-t border-border/60 mt-12">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 text-sm text-white/50 flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} Culinaire Specialist. Fine dining kitchen operations, built for chefs.</div>
          <Link to="/register" className="text-white/70 hover:text-white">Start your kitchen</Link>
        </div>
      </footer>
    </div>
  );
}

// Reusable data table.
export function DataTable({ caption, columns, rows, testId }) {
  return (
    <div className="rounded-2xl glass-card overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm min-w-[560px]">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr className="text-left border-b border-white/10 bg-white/[0.03]">
            {columns.map((c) => (
              <th key={c} className="px-4 py-3 label-eyebrow font-medium">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-white/5 last:border-b-0">
              {r.map((cell, j) => (
                <td key={j} className={`px-4 py-3 ${j === 0 ? "text-white/85" : "text-white/70 tabular"}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// A single Q&A section: 50-word factual answer first, then a soft
// callout that mentions the software. Enforced with a small helper.
export function AnswerBlock({ id, heading, answer, softwareNote, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display text-2xl sm:text-3xl tracking-tight">{heading}</h2>
      <p className="mt-4 text-white/85 leading-relaxed">{answer}</p>
      {children}
      {softwareNote ? (
        <div
          className="mt-6 rounded-xl px-4 py-3 text-sm"
          style={{
            background: "linear-gradient(180deg, rgba(232,207,154,0.09), rgba(232,207,154,0.02))",
            border: "1px solid rgba(232,207,154,0.24)",
            color: "rgba(240, 232, 216, 0.9)",
          }}
        >
          {softwareNote}
        </div>
      ) : null}
    </section>
  );
}
